import { Component, Input, Output, EventEmitter, SimpleChanges, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { NgSelectModule } from '@ng-select/ng-select';
import { MailTemplatesService } from '../../../../core/services/mail-templates/mail-templates.service';
import { AccountSettingsService } from '../../../../core/services/account-settings/account-settings.service';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { InboxService } from '../../../../core/services/inbox/inbox.service';

@Component({
  selector: 'app-send-mail-panel',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, NgSelectModule],
  templateUrl: './send-mail-panel.component.html',
  styleUrl: './send-mail-panel.component.css',
})
export class SendMailPanelComponent {
  @Input() visible = false;
  @Input() clientReference = '';
  @Input() clientEmail = '';
  @Input() clientName = '';
  @Output() closed = new EventEmitter<void>();
  @Output() mailSent = new EventEmitter<void>();

  @ViewChild('bodyEditor') bodyEditor!: ElementRef<HTMLDivElement>;

  mailTemplates: any[] = [];
  selectedTemplate: any = null;
  mailEditSubject = '';
  mailEditBody = '';
  mailOriginalSubject = '';
  mailOriginalBody = '';
  mailAttachments: File[] = [];

  // IMAP thread search
  threadMessages: any[] = [];
  threadLoading = false;
  threadSearchDone = false;
  threadListOpen = false;

  // Reply mode
  replyMode = false;
  replyUid: number | null = null;
  replySubject = '';
  fullThread: any[] = [];
  fullThreadLoading = false;

  constructor(
    private mailTemplatesService: MailTemplatesService,
    private accountSettingsService: AccountSettingsService,
    private alertService: AlertService,
    private inboxService: InboxService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.resetState();
      this.loadTemplates();
      this.searchThreads();
    }
  }

  private resetState(): void {
    this.selectedTemplate = null;
    this.mailEditSubject = '';
    this.mailEditBody = '';
    this.mailOriginalSubject = '';
    this.mailOriginalBody = '';
    this.mailAttachments = [];
    this.threadMessages = [];
    this.threadLoading = false;
    this.threadSearchDone = false;
    this.threadListOpen = false;
    this.replyMode = false;
    this.replyUid = null;
    this.replySubject = '';
    this.fullThread = [];
    this.fullThreadLoading = false;
  }

  private loadTemplates(): void {
    this.mailTemplatesService.getAvailableTemplates(1, 100).subscribe({
      next: (response) => { this.mailTemplates = response.data || []; },
      error: () => { this.mailTemplates = []; },
    });
  }

  private searchThreads(): void {
    if (!this.clientEmail) return;
    this.threadLoading = true;
    this.inboxService.getMessages({ search: this.clientEmail, perPage: 10 }).subscribe({
      next: (response) => {
        this.threadMessages = response.data || [];
        this.threadLoading = false;
        this.threadSearchDone = true;
      },
      error: () => {
        this.threadMessages = [];
        this.threadLoading = false;
        this.threadSearchDone = true;
      },
    });
  }

  getFromDisplay(from: any): string {
    if (!from) return '';
    if (Array.isArray(from)) {
      if (from.length === 0) return '';
      return from[0].name || from[0].email || '';
    }
    return from.name || from.email || '';
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    const now = new Date();
    const isToday = d.toDateString() === now.toDateString();
    if (isToday) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: '2-digit' })
      + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  loadThreadAndReply(msg: any, event: Event): void {
    event.stopPropagation();
    this.fullThreadLoading = true;
    this.replyUid = msg.uid;
    this.replySubject = msg.subject || '';
    this.mailEditSubject = msg.subject ? (msg.subject.startsWith('Re:') ? msg.subject : 'Re: ' + msg.subject) : '';

    this.inboxService.getThread(msg.uid, msg.folder || 'inbox').subscribe({
      next: (response) => {
        this.fullThread = response.data || [];
        this.fullThreadLoading = false;
        this.replyMode = true;
        this.threadListOpen = false;
        // Limpiar body para escribir respuesta
        this.mailEditBody = '';
        setTimeout(() => {
          if (this.bodyEditor) {
            this.bodyEditor.nativeElement.innerHTML = '';
          }
        });
      },
      error: () => {
        this.fullThreadLoading = false;
        this.alertService.error('swal_messages.general.error');
      },
    });
  }

  exitReplyMode(): void {
    this.replyMode = false;
    this.replyUid = null;
    this.replySubject = '';
    this.fullThread = [];
    this.mailEditSubject = '';
    this.mailEditBody = '';
    this.mailAttachments = [];
    setTimeout(() => {
      if (this.bodyEditor) {
        this.bodyEditor.nativeElement.innerHTML = '';
      }
    });
  }

  onTemplateSelected(): void {
    if (this.selectedTemplate) {
      this.mailOriginalSubject = this.selectedTemplate.subject || '';
      this.mailOriginalBody = this.selectedTemplate.body_html || '';
      this.mailEditSubject = this.mailOriginalSubject;
      this.mailEditBody = this.mailOriginalBody;
      setTimeout(() => {
        if (this.bodyEditor) {
          this.bodyEditor.nativeElement.innerHTML = this.mailEditBody;
        }
      });
    }
  }

  // Barra de formato
  execCommand(command: string, value?: string): void {
    document.execCommand(command, false, value);
    this.bodyEditor?.nativeElement.focus();
  }

  onBodyInput(): void {
    this.mailEditBody = this.bodyEditor?.nativeElement.innerHTML || '';
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.mailAttachments.push(...Array.from(input.files));
      input.value = '';
    }
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    (event.target as HTMLElement).classList.remove('over');
    if (event.dataTransfer?.files) {
      this.mailAttachments.push(...Array.from(event.dataTransfer.files));
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    (event.target as HTMLElement).classList.add('over');
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    (event.target as HTMLElement).classList.remove('over');
  }

  removeAttachment(index: number): void {
    this.mailAttachments.splice(index, 1);
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  }

  getThumbnailSrc(file: File): string {
    const mimeToIcon: Record<string, string> = {
      'image/jpeg': '../../../assets/icons/jpg.svg',
      'image/png': '../../../assets/icons/jpg.svg',
      'application/pdf': '../../../assets/icons/pdf.svg',
      'application/msword': '../../../assets/icons/doc.svg',
      'text/csv': '../../../assets/icons/file.svg',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '../../../assets/icons/doc.svg',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '../../../assets/icons/xls.svg',
      'application/vnd.ms-excel': '../../../assets/icons/xls.svg',
    };
    return mimeToIcon[file.type] || '../../../assets/icons/file.svg';
  }

  canSend(): boolean {
    return !!this.clientReference && (!!this.mailEditSubject.trim() || !!this.mailEditBody.trim());
  }

  sendMail(): void {
    if (!this.canSend()) return;
    this.alertService.loading('swal_messages.please_wait');

    if (this.replyMode && this.replyUid) {
      // Reply al hilo existente
      const attachments = this.mailAttachments.length > 0 ? this.mailAttachments : undefined;
      this.inboxService.reply(this.replyUid, this.mailEditBody, attachments).subscribe({
        next: () => {
          this.alertService.successOk('swal_messages.account_settings.mail_sent').then(() => {
            this.mailSent.emit();
          });
        },
        error: (error) => {
          this.alertService.error(error.error);
        },
      });
    } else {
      // Correo nuevo — siempre multipart/form-data
      const fd = new FormData();
      fd.append('client_references[]', this.clientReference);
      fd.append('subject', this.mailEditSubject);
      fd.append('body_html', this.mailEditBody);
      this.mailAttachments.forEach((f) => fd.append('attachments[]', f, f.name));

      if (this.selectedTemplate) {
        fd.append('template_reference', this.selectedTemplate.reference);
      } else {
        // branding inline como objeto con notación de corchetes
        fd.append('branding[primary_color]', '');
      }

      this.accountSettingsService.sendMail(fd).subscribe({
        next: () => {
          this.alertService.successOk('swal_messages.account_settings.mail_sent').then(() => {
            this.mailSent.emit();
          });
        },
        error: (error) => { this.alertService.error(error.error); },
      });
    }
  }

  close(): void {
    this.closed.emit();
  }
}
